// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PageBackNavigation from '../components/PageBackNavigation';
import StandardSearch, { matchesStandardSearch } from '../components/StandardSearch';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAdminStudentDetail, fetchParentStudents, fetchSchoolDisplaySettings, fetchStudentProgressionGrid, fetchStudentProgressPdf, fetchTeacherStudentDetail } from '../services/api';
import BackToTop from '../components/BackToTop';
import IreadyScoreBoxes from '../components/IreadyScoreBoxes';
import { useAuth } from '../context/AuthContext';
import { isReadOnlyTeacherDemo } from '../utils/demoAccess';
import InlineAtHomeSupportPanel from '../components/InlineAtHomeSupportPanel';
import DataPointDots, { collectDataPointHistory } from '../components/DataPointDots';
import './ClearViewPage.css';
import {
  UI_COLORS,
  SUBJECT_PRIORITY,
  CORE_GRADE_ORDER,
  normalizedGradeLabel,
  gradeSort,
  gradeFloor,
  gradeIndex,
  statusFromScore,
  statusPriority,
  summaryStatus,
  standardProgressScore,
  aggregateFamilyGrade,
  statusColor,
  canonicalSubject,
  normalizeLiteracyWritingStrand,
  resolveStrandLabel,
} from '../utils/progressStatus';
import { fetchWithRetry } from '../utils/requestRetry';
import {
  buildGradeColumns,
  expandGradeRange,
  getStandardApplicableGrades,
  getStandardGradeColumnKey,
  sortGradeColumns,
} from '../utils/gradeColumns';
import { fetchFamilyDescriptions } from '../utils/familyDescriptions';
import { fetchAllVocabBanks, HighlightVocabText } from '../utils/vocabBank';
import GradingLegend from '../components/GradingLegend';

// Set to true when the mastery gate should be re-enabled.
const ENABLE_GRADE_MASTERY_LOCK = false;
// Maps a subject panel name to its i-Ready subject key (only Literacy and Math have i-Ready).
const IREADY_SUBJECT_BY_PANEL = { literacy: 'reading', math: 'math', mathematics: 'math' };

// Printed Resources (support-bank downloadable file matching) is temporarily disabled;
// re-add RESOURCE_FILE_LABEL_ALIASES/EXTERNAL_RESOURCE_LINKS/normalizeLookupName if restored.

function pctMastered(counts) {
  if (!counts || !counts.total) return 0;
  return Math.round(((counts.mastered || 0) / counts.total) * 100);
}

function hasAssessedProgress(counts) {
  return ['mastered', 'on_track', 'working', 'not_yet']
    .some((status) => (counts?.[status] || 0) > 0);
}

function gradeRange(grade) {
  const token = String(grade || '').trim().toUpperCase();
  const parts = token.split('-').map((part) => part.trim());
  const start = CORE_GRADE_ORDER.indexOf(parts[0]);
  const end = CORE_GRADE_ORDER.indexOf(parts[1] || parts[0]);
  return start >= 0 && end >= start ? { start, end } : null;
}

function isGradeCoveredByBand(grade, band) {
  const range = gradeRange(band);
  const gradeIndexValue = CORE_GRADE_ORDER.indexOf(String(grade || '').toUpperCase());
  return Boolean(range && range.start !== range.end && gradeIndexValue >= range.start && gradeIndexValue <= range.end);
}

function getFamilyStrandLabel(standard, fallback) {
  const sourceLabel = String(fallback || '').trim();
  const normalizedWritingStrand = normalizeLiteracyWritingStrand(
    standard?.subject,
    standard?.grade_level,
    standard?.mls_code,
    sourceLabel,
  );
  if (normalizedWritingStrand) return normalizedWritingStrand;
  return sourceLabel || 'General';
}

// A single-hue progress bar: solid color reflects the tile's overall status
// (mastered/working/not_yet/na), fill width reflects percent mastered, with a
// pale tint of the same color for the remaining track.
function SegmentedProgressBar({ status, counts, getColor, height = 22 }: any) {
  const color = getColor(status || 'na');
  const percent = pctMastered(counts);
  return (
    <div style={{ position: 'relative', height, borderRadius: 999, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.06)' }}>
      <div style={{ position: 'absolute', inset: 0, background: color, opacity: 0.3 }} />
      <div style={{ position: 'absolute', inset: 0, width: `${percent}%`, background: color }} />
    </div>
  );
}

const STATUS_MARKER_LABELS = { mastered: 'Proficient', on_track: 'On Track', working: 'Learning Zone', not_yet: 'Not Yet', na: 'Not Assessed' };

const MARKER_SIZES: Record<string, { width: number; height: number; gap: number; border: number; className: string }> = {
  pill: { width: 26, height: 14, gap: 5, border: 1.5, className: 'clear-progress-strand-pill' },
  gradePill: { width: 20, height: 11, gap: 4, border: 1.5, className: 'clear-progress-grade-pill' },
  gradePillCompact: { width: 13, height: 8, gap: 3, border: 1, className: 'clear-progress-grade-pill' },
  dot: { width: 14, height: 14, gap: 4, border: 1.5, className: 'clear-progress-standard-dot' },
};

function StatusMarkers({ items, getColor, label, variant, id, align = 'flex-start', decorative = false }: any) {
  const size = MARKER_SIZES[variant] || MARKER_SIZES.dot;
  return (
    <div id={id} role="group" aria-label={label} aria-hidden={decorative || undefined} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: align, gap: size.gap, marginBottom: 0 }}>
      {items.map((item) => {
        const statusLabel = STATUS_MARKER_LABELS[item.status] || STATUS_MARKER_LABELS.na;
        return (
          <span
            key={item.key}
            role="img"
            aria-label={`${item.name}: ${statusLabel}`}
            title={`${item.name}: ${statusLabel}`}
            className={size.className}
            style={{
              display: 'inline-block',
              flexShrink: 0,
              width: size.width,
              height: size.height,
              borderRadius: 999,
              background: getColor(item.status || 'na'),
              border: `${size.border}px solid rgba(255,255,255,0.95)`,
              boxShadow: '0 0 0 1px rgba(15,23,42,0.25)',
              boxSizing: 'border-box',
            }}
          />
        );
      })}
    </div>
  );
}

export default function ClearViewPage({ studentId, classId, studentName, role, previewAsFamily = false, snapshotOnly = false, onSnapshotClick = null }: any) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Admin gets the same family-friendly experience as parents, not the teacher grade-entry view.
  // Teachers opt into the same layout via previewAsFamily to see exactly what a family account sees.
  const isFamilyLikeView = role === 'parent' || role === 'family' || role === 'admin' || previewAsFamily;
  const showFamilyDescriptions = role === 'parent' || role === 'family' || previewAsFamily;
  // Leadership's display simplifications only ever apply to family/student audiences, never admin —
  // but a teacher's "view as parent" preview should also honor them, since it mirrors what a family sees.
  const isRestrictedAudience = role === 'parent' || role === 'family' || role === 'student' || previewAsFamily;
  const [displaySettings, setDisplaySettings] = useState({ hide_percent_for_family_student: false, gray_out_red_for_family_student: false });
  const hidePercent = isRestrictedAudience && displaySettings.hide_percent_for_family_student;
  const grayOutRed = isRestrictedAudience && displaySettings.gray_out_red_for_family_student;
  const displayStatusColor = useCallback(
    (status) => (grayOutRed && status === 'not_yet' ? UI_COLORS.nA : statusColor(status)),
    [grayOutRed]
  );
  const tileTextColor = (status, fallback) => (
    showFamilyDescriptions && (status === 'mastered' || (status === 'not_yet' && !grayOutRed))
      ? '#fff' : fallback
  );
  const [rows, setRows] = useState<any>([]);
  const [activeClassRows, setActiveClassRows] = useState<any>([]);
  const [familyDescriptions, setFamilyDescriptions] = useState<any>({});
  const [vocabBanks, setVocabBanks] = useState<any>({});
  const [studentGrade, setStudentGrade] = useState<any>('');
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [isGeneratingReport, setIsGeneratingReport] = useState<any>(false);
  const [reportError, setReportError] = useState<any>('');
  // Interactive drill-down: subject -> domain (per grade) -> strand -> cluster -> standards.
  // Each level below "expandedSubjects" tracks a single open key at a time.
  const [expandedSubjects, setExpandedSubjects] = useState<any>({});
  const [standardSearch, setStandardSearch] = useState<any>('');
  const [selectedGradeBySubject, setSelectedGradeBySubject] = useState<any>({});
  const gradeTileRefs = useRef<any>({});
  const gradeCarouselRefs = useRef<any>({});
  const [expandedDomainGrade, setExpandedDomainGrade] = useState<any>(null);
  const [expandedStrand, setExpandedStrand] = useState<any>(null);
  const [selectedSupportStandard, setSelectedSupportStandard] = useState<any>(null);
  const hasRestoredReturnState = useRef(false);
  const hasLoadedRowsRef = useRef(false);
  const returnProgressState = location.state?.progressState || null;

  const toggleSubjectExpanded = (subject) => {
    setExpandedSubjects((prev) => ({ ...prev, [subject]: !prev[subject] }));
  };

  const toggleDomainGrade = (subject, domain, grade) => {
    const key = `${subject}|${domain}|${grade}`;
    setExpandedDomainGrade((prev) => (prev === key ? null : key));
    setExpandedStrand(null);
  };

  const toggleStrand = (domainGradeKey, strand) => {
    const key = `${domainGradeKey}|${strand}`;
    setExpandedStrand((prev) => (prev === key ? null : key));
  };

  const loadGrid = useCallback(async ({ silent = false } = {}) => {
    if (!studentId) return;
    if (!silent) setLoading(true);
    try {
      if (isFamilyLikeView && classId) {
        const [classRowsResult, allRowsResult] = await Promise.allSettled([
          fetchWithRetry(() => fetchStudentProgressionGrid(studentId, classId)),
          fetchWithRetry(() => fetchStudentProgressionGrid(studentId)),
        ]);

        // Both failing is a load error, not an empty record for this student.
        if (classRowsResult.status === 'rejected' && allRowsResult.status === 'rejected') {
          throw classRowsResult.reason || allRowsResult.reason || new Error('Failed to load progress');
        }

        const classRows = classRowsResult.status === 'fulfilled' && Array.isArray(classRowsResult.value)
          ? classRowsResult.value
          : [];
        const allRows = allRowsResult.status === 'fulfilled' && Array.isArray(allRowsResult.value)
          ? allRowsResult.value
          : classRows;

        setRows(allRows);
        setActiveClassRows(classRows.length ? classRows : allRows);
        hasLoadedRowsRef.current = allRows.length > 0;
        setError(allRows.length ? '' : 'No progress data found for this student yet.');
      } else {
        const data = await fetchWithRetry(() => fetchStudentProgressionGrid(studentId, classId));
        const nextRows = Array.isArray(data) ? data : [];
        setRows(nextRows);
        setActiveClassRows(nextRows);
        hasLoadedRowsRef.current = nextRows.length > 0;
        setError('');
      }
    } catch (_err: any) {
      // Leave any rows already on screen in place so a failed refresh can't blank the page.
      if (!hasLoadedRowsRef.current) {
        setError('Could not load progress data.');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [studentId, classId, isFamilyLikeView]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  useEffect(() => {
    if (!showFamilyDescriptions) return undefined;

    let cancelled = false;
    fetchFamilyDescriptions()
      .then((descriptions) => {
        if (!cancelled) setFamilyDescriptions(descriptions);
      })
      .catch((loadError) => {
        if (!cancelled) console.error('Could not load family-friendly standards descriptions.', loadError);
      });

    return () => {
      cancelled = true;
    };
  }, [showFamilyDescriptions]);

  useEffect(() => {
    if (!showFamilyDescriptions) return undefined;

    let cancelled = false;
    fetchAllVocabBanks()
      .then((banks) => {
        if (!cancelled) setVocabBanks(banks);
      })
      .catch((loadError) => {
        if (!cancelled) console.error('Could not load vocabulary banks.', loadError);
      });

    return () => {
      cancelled = true;
    };
  }, [showFamilyDescriptions]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState === 'visible') {
        loadGrid({ silent: true });
      }
    };

    const refreshInterval = setInterval(() => loadGrid({ silent: true }), 15000);
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);

    return () => {
      clearInterval(refreshInterval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [loadGrid]);

  useEffect(() => {
    if (!isFamilyLikeView || !studentId) return;

    let cancelled = false;
    const gradeLookup = role === 'admin'
      ? fetchAdminStudentDetail(studentId).then((data) => data?.student?.grade_level ?? null)
      : role === 'teacher'
      ? fetchTeacherStudentDetail(studentId).then((data) => data?.student?.grade_level ?? null)
      : fetchParentStudents().then((data) => {
          const match = (data?.students || []).find((student) => String(student.student_id) === String(studentId));
          return match?.grade_level ?? null;
        });

    gradeLookup
      .then((gradeLevel) => {
        if (cancelled) return;
        if (gradeLevel !== undefined && gradeLevel !== null) {
          setStudentGrade(String(gradeLevel));
        }
      })
      .catch(() => {
        if (!cancelled) setStudentGrade('');
      });

    return () => {
      cancelled = true;
    };
  }, [role, isFamilyLikeView, studentId]);

  useEffect(() => {
    if (!isRestrictedAudience || !user?.school_id) return;

    let cancelled = false;
    fetchSchoolDisplaySettings(user.school_id)
      .then((data) => {
        if (cancelled || !data) return;
        setDisplaySettings({
          hide_percent_for_family_student: !!data.hide_percent_for_family_student,
          gray_out_red_for_family_student: !!data.gray_out_red_for_family_student,
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isRestrictedAudience, user?.school_id]);

  const subjectSummaries = useMemo(() => {
    const sourceRows = isFamilyLikeView && activeClassRows.length
      ? activeClassRows
      : rows;

    const map = {};

    // Family view shows Science per grade, so banded standards (e.g. NGSS MS / 3-5-ETS1) appear in each grade they cover.
    const gradedRows = sourceRows.flatMap((entry) => {
      const standard = entry?.standard || {};
      const applicableGrades = getStandardApplicableGrades(standard.grade_level, standard.mls_code);
      const spreadBand = isFamilyLikeView
        && String(canonicalSubject(standard.subject)).toLowerCase() === 'science'
        && applicableGrades.length > 1;
      if (spreadBand) return applicableGrades.map((grade) => ({ entry, grade }));
      return [{ entry, grade: getStandardGradeColumnKey(standard.grade_level, standard.mls_code) || 'N/A' }];
    });

    gradedRows.forEach(({ entry, grade }) => {
      const standard = entry?.standard || {};
      const point = entry?.data_point || {};
      const subject = canonicalSubject(standard.subject);
      const strand = getFamilyStrandLabel(
        standard,
        resolveStrandLabel(standard, 'Skill'),
      );
      const standardCode = String(standard.mls_code || '').trim();
      const familyDescription = showFamilyDescriptions ? familyDescriptions[standardCode] : null;
      const friendlyDescriptions = familyDescription?.subject === subject ? familyDescription : null;
      const clusterNumber = String(standard.cluster_and_expectation_number || '').trim();
      const detail = standard.cluster_expectation_description || '';
      const status = statusFromScore(standardProgressScore(point));

      if (!map[subject]) {
        map[subject] = {};
      }
      if (!map[subject][grade]) {
        map[subject][grade] = {
          grade,
          status: 'na',
          skillsNeedingSupport: [],
          strands: [],
          domains: {},
          counts: { total: 0, mastered: 0, on_track: 0, working: 0, not_yet: 0, na: 0 },
        };
      }

      const gradeRow = map[subject][grade];
      gradeRow.counts.total += 1;
      gradeRow.counts[status] = (gradeRow.counts[status] || 0) + 1;
      if (statusPriority(status) > statusPriority(gradeRow.status)) {
        gradeRow.status = status;
      }

      const strandLabel = String(strand).trim();
      const domainName = String(standard.domain_description || standard.domain || 'General').trim() || 'General';

      if (!gradeRow.domains[domainName]) {
        gradeRow.domains[domainName] = {
          domain: domainName,
          status: 'na',
          familyDescription: '',
          counts: { total: 0, mastered: 0, on_track: 0, working: 0, not_yet: 0, na: 0 },
          strands: {},
        };
      }
      const domainRow = gradeRow.domains[domainName];
      if (!domainRow.familyDescription && friendlyDescriptions?.domain) {
        domainRow.familyDescription = friendlyDescriptions.domain;
      }
      domainRow.counts.total += 1;
      domainRow.counts[status] = (domainRow.counts[status] || 0) + 1;
      if (statusPriority(status) > statusPriority(domainRow.status)) {
        domainRow.status = status;
      }

      // Nested drill-down: domain -> strand -> cluster -> individual standards.
      const realStrandName = getFamilyStrandLabel(
        standard,
        resolveStrandLabel(standard, 'General'),
      );
      if (!domainRow.strands[realStrandName]) {
        domainRow.strands[realStrandName] = {
          strand: realStrandName,
          status: 'na',
          familyDescription: '',
          counts: { total: 0, mastered: 0, on_track: 0, working: 0, not_yet: 0, na: 0 },
          clusters: {},
        };
      }
      const strandRow = domainRow.strands[realStrandName];
      if (!strandRow.familyDescription && friendlyDescriptions?.strand) {
        strandRow.familyDescription = friendlyDescriptions.strand;
      }
      strandRow.counts.total += 1;
      strandRow.counts[status] = (strandRow.counts[status] || 0) + 1;
      if (statusPriority(status) > statusPriority(strandRow.status)) {
        strandRow.status = status;
      }

      const clusterLabel = String(standard.cluster_expectation_description || '').trim() || realStrandName;
      if (!strandRow.clusters[clusterLabel]) {
        strandRow.clusters[clusterLabel] = {
          cluster: clusterLabel,
          status: 'na',
          counts: { total: 0, mastered: 0, on_track: 0, working: 0, not_yet: 0, na: 0 },
          standards: [],
        };
      }
      const clusterRow = strandRow.clusters[clusterLabel];
      clusterRow.counts.total += 1;
      clusterRow.counts[status] = (clusterRow.counts[status] || 0) + 1;
      if (statusPriority(status) > statusPriority(clusterRow.status)) {
        clusterRow.status = status;
      }

      const existingLeaf = clusterRow.standards.find((item) => item.standardCode === standardCode);
      if (existingLeaf) {
        if (statusPriority(status) > statusPriority(existingLeaf.status)) {
          existingLeaf.status = status;
        }
      } else {
        clusterRow.standards.push({
          label: clusterNumber ? `Standard ${clusterNumber}` : (standardCode || 'Standard'),
          detail: standard.standard_description || '',
          familyDescription: friendlyDescriptions?.standard || '',
          familySupport: friendlyDescriptions || null,
          standardCode,
          domain: domainName,
          strand: realStrandName,
          clusterNumber,
          status,
          aggregateScore: standardProgressScore(point),
          history: Array.isArray(point.history) ? point.history : [],
        });
      }

      if (strandLabel) {
        const existingStrand = gradeRow.strands.find((item) => item.label === strandLabel);
        if (existingStrand) {
          if (statusPriority(status) > statusPriority(existingStrand.status)) {
            existingStrand.status = status;
            existingStrand.detail = detail;
            existingStrand.standardCode = standardCode;
            existingStrand.domain = domainName;
            existingStrand.clusterNumber = clusterNumber;
          }
        } else {
          gradeRow.strands.push({
            label: strandLabel,
            detail,
            standardCode,
            domain: domainName,
            clusterNumber,
            status,
          });
        }
      }

      if (status === 'working' || status === 'not_yet') {
        const skillLabel = String(strand).trim();
        if (skillLabel && !gradeRow.skillsNeedingSupport.some((s) => s.label === skillLabel)) {
          gradeRow.skillsNeedingSupport.push({
            label: skillLabel,
            detail,
            standardCode: String(standard.mls_code || '').trim(),
            domain: domainName,
            clusterNumber,
          });
        }
      }
    });

    return Object.entries(map)
      .map(([subject, gradeMap]) => {
        const studentGradeIdx = gradeIndex(studentGrade);
        const floorStats = {};
        Object.values(gradeMap).forEach((gradeRow) => {
          const floor = gradeFloor(gradeRow.grade);
          if (!floor) return;
          if (!floorStats[floor]) {
            floorStats[floor] = { total: 0, mastered: 0 };
          }
          floorStats[floor].total += gradeRow.counts.total;
          floorStats[floor].mastered += gradeRow.counts.mastered;
        });

        // Grade-band standards keep one shared tile (e.g. 9-12) instead of being copied into every grade.
        const gradesWithData = Object.values(gradeMap).filter((gradeRow) => (
          gradeRow.counts.total > 0 && (isFamilyLikeView || gradeRow.status !== 'na')
        ));
        const gradeOrder = sortGradeColumns(gradesWithData
          .filter((gradeRow) => gradeRange(gradeRow.grade))
          .map((gradeRow) => ({ key: gradeRow.grade, ...gradeRange(gradeRow.grade) })))
          .map((column) => column.key);
        const gradeOrderIndex = (grade) => {
          const index = gradeOrder.indexOf(grade);
          return index === -1 ? 999 : index;
        };

        const grades = gradesWithData
          .sort((a, b) => gradeOrderIndex(a.grade) - gradeOrderIndex(b.grade))
          .map((gradeRow) => {
            if (showFamilyDescriptions) aggregateFamilyGrade(gradeRow);
            const currentIdx = gradeIndex(gradeRow.grade);
            let assessmentLock = { locked: false, reason: '' };

            if (ENABLE_GRADE_MASTERY_LOCK && studentGradeIdx >= 0 && currentIdx > studentGradeIdx) {
              const priorGrade = CORE_GRADE_ORDER[currentIdx - 1];
              const priorStats = floorStats[priorGrade];
              if (priorStats && priorStats.total > 0) {
                const priorMasteryPct = Math.round((priorStats.mastered / priorStats.total) * 100);
                if (priorMasteryPct < 50) {
                  assessmentLock = {
                    locked: true,
                    reason: `${priorGrade} must reach at least 50% mastery first (currently ${priorMasteryPct}%).`,
                  };
                }
              }
            }

            return {
              ...gradeRow,
              assessmentLock,
            };
          });
        const displayGrades = isFamilyLikeView
          ? (() => {
            const gradesByKey = Object.fromEntries(grades.map((gradeRow) => [gradeRow.grade, gradeRow]));
            const columns = buildGradeColumns(
              grades.map((gradeRow) => ({ applicableGrades: expandGradeRange(gradeRow.grade) })),
              { includeUncoveredGrades: true },
            ).filter((column) => column.isBand || CORE_GRADE_ORDER.includes(column.key));
            return columns.map(({ key: grade }) => gradesByKey[grade] || ({
              grade,
              status: 'na',
              skillsNeedingSupport: [],
              strands: [],
              domains: {},
              counts: { total: 0, mastered: 0, on_track: 0, working: 0, not_yet: 0, na: 0 },
              assessmentLock: { locked: false, reason: '' },
            }));
          })()
          : grades;

        return {
          subject,
          displayGrades,
          grades: grades.map((gradeRow) => ({
            ...gradeRow,
            strands: [...gradeRow.strands].sort((a, b) => {
              const statusDiff = statusPriority(b.status) - statusPriority(a.status);
              if (statusDiff !== 0) return statusDiff;
              return String(a.label).localeCompare(String(b.label));
            }),
          })),
          // Transposes each grade's domain breakdown into domain -> grade rows so a
          // domain can render its own segmented bar across every grade, nested one
          // level under the subject (interactive drill-down: subject -> domain -> grade -> strand -> cluster -> standards).
          domains: (() => {
            const domainMap = {};
            grades.forEach((gradeRow) => {
              Object.values(gradeRow.domains || {}).forEach((domainRow) => {
                if (!domainMap[domainRow.domain]) {
                  domainMap[domainRow.domain] = { domain: domainRow.domain, byGrade: {} };
                }
                domainMap[domainRow.domain].byGrade[gradeRow.grade] = {
                  status: domainRow.status,
                  ...(showFamilyDescriptions ? { aggregateScore: domainRow.aggregateScore } : {}),
                  counts: domainRow.counts,
                  familyDescription: domainRow.familyDescription,
                  assessmentLock: gradeRow.assessmentLock,
                  strands: Object.values(domainRow.strands)
                    .map((strandRow) => ({
                      ...strandRow,
                      clusters: Object.values(strandRow.clusters)
                        .map((clusterRow) => ({
                          ...clusterRow,
                          standards: [...clusterRow.standards].sort((a, b) => {
                            const statusDiff = statusPriority(b.status) - statusPriority(a.status);
                            if (statusDiff !== 0) return statusDiff;
                            return String(a.label).localeCompare(String(b.label));
                          }),
                        }))
                        .sort((a, b) => {
                          const statusDiff = statusPriority(b.status) - statusPriority(a.status);
                          if (statusDiff !== 0) return statusDiff;
                          return String(a.cluster).localeCompare(String(b.cluster));
                        }),
                    }))
                    .sort((a, b) => {
                      const statusDiff = statusPriority(b.status) - statusPriority(a.status);
                      if (statusDiff !== 0) return statusDiff;
                      return String(a.strand).localeCompare(String(b.strand));
                    }),
                };
              });
            });
            return Object.values(domainMap).sort((a, b) => a.domain.localeCompare(b.domain));
          })(),
          progressPct: grades.length
            ? Math.round(
                (grades.reduce((sum, g) => sum + g.counts.mastered, 0)
                  / Math.max(1, grades.reduce((sum, g) => (
                    sum + g.counts.mastered + g.counts.on_track + g.counts.working + g.counts.not_yet
                  ), 0))) * 100,
              )
            : 0,
        };
      })
      .sort((a, b) => {
        const ap = SUBJECT_PRIORITY[String(a.subject || '').toLowerCase()] || 99;
        const bp = SUBJECT_PRIORITY[String(b.subject || '').toLowerCase()] || 99;
        if (ap !== bp) return ap - bp;
        return String(a.subject).localeCompare(String(b.subject));
      });
  }, [rows, activeClassRows, familyDescriptions, isFamilyLikeView, showFamilyDescriptions, studentGrade]);

  // Flattens every subject/grade into an overall mastery percentage plus a
  // prioritized list of strands still needing support, for a quick snapshot.
  const focusSnapshot = useMemo(() => {
    let totalMastered = 0;
    let totalCount = 0;
    const priorityStrands = [];

    subjectSummaries.forEach(({ subject, grades }) => {
      grades.forEach((gradeRow) => {
        totalMastered += gradeRow.counts.mastered;
        totalCount += gradeRow.counts.mastered
          + gradeRow.counts.on_track
          + gradeRow.counts.working
          + gradeRow.counts.not_yet;
        if (gradeRow.assessmentLock?.locked) return;
        gradeRow.strands.forEach((strand) => {
          if (strand.status !== 'not_yet' && strand.status !== 'working') return;
          priorityStrands.push({ subject, grade: gradeRow.grade, ...strand });
        });
      });
    });

    priorityStrands.sort((a, b) => {
      const diff = statusPriority(b.status) - statusPriority(a.status);
      if (diff !== 0) return diff;
      return gradeSort(a.grade, b.grade);
    });

    return {
      overallPct: totalCount ? Math.round((totalMastered / totalCount) * 100) : 0,
      totalMastered,
      totalCount,
      topPriorities: priorityStrands.slice(0, 5),
    };
  }, [subjectSummaries]);

  useEffect(() => {
    const animationFrame = window.requestAnimationFrame(() => {
      subjectSummaries.forEach(({ subject, grades, displayGrades = grades }) => {
        const defaultGrade = displayGrades.find((grade) => (
          gradeFloor(grade.grade) === gradeFloor(studentGrade)
          || isGradeCoveredByBand(studentGrade, grade.grade)
        ))
          || displayGrades.find((grade) => grade.status === 'working')
          || displayGrades[0];
        const selectedGrade = selectedGradeBySubject[subject] || defaultGrade?.grade;
        const tile = gradeTileRefs.current[`${subject}|${selectedGrade}`];
        const carousel = gradeCarouselRefs.current[subject];
        if (tile && carousel) {
          const tileRect = tile.getBoundingClientRect();
          const carouselRect = carousel.getBoundingClientRect();
          const tileCenterInScrollArea = carousel.scrollLeft + tileRect.left - carouselRect.left + (tileRect.width / 2);
          const targetScrollLeft = tileCenterInScrollArea - (carousel.clientWidth / 2);
          carousel.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' });
        }
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [subjectSummaries, selectedGradeBySubject, studentGrade]);

  useEffect(() => {
    if (!returnProgressState || !rows.length || hasRestoredReturnState.current) return;

    setExpandedSubjects(returnProgressState.expandedSubjects || {});
    setSelectedGradeBySubject(returnProgressState.selectedGradeBySubject || {});
    setExpandedDomainGrade(returnProgressState.expandedDomainGrade || null);
    setExpandedStrand(returnProgressState.expandedStrand || null);
    hasRestoredReturnState.current = true;

    const restoreScrollY = Number(returnProgressState.scrollY);
    if (Number.isFinite(restoreScrollY)) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.scrollTo({ top: restoreScrollY, left: 0, behavior: 'auto' });
        });
      });
    }
  }, [returnProgressState, rows.length]);

  const studentLabel = studentName || `Child #${studentId}`;
  const studentInitials = String(studentLabel)
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'S';
  const studentGradeLabel = normalizedGradeLabel(studentGrade);

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    setReportError('');
    try {
      const blob = await fetchStudentProgressPdf(studentId, classId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safeStudentName = String(studentLabel)
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_-]/g, '');
      link.href = url;
      link.download = `${safeStudentName || 'student'}_progress_snapshot.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error: any) {
      setReportError('Could not generate the report. Please try again.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (loading) return <div style={{ padding: 24, color: 'var(--ae-text-muted)', textAlign: 'center' }}>Loading progress…</div>;
  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>
        <button
          type="button"
          onClick={() => loadGrid()}
          style={{ border: '1px solid var(--ae-border)', background: 'var(--ae-surface)', color: 'var(--ae-primary-strong)', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }
  if (!rows.length) return <div style={{ color: 'var(--ae-text-muted)', textAlign: 'center', padding: 28 }}>No progress data available yet.</div>;

  const goToSupport = (subject, grade, skill, standardCode, domain, nestedContext = {}) => {
    const params = new URLSearchParams();
    if (subject) params.set('subject', subject);
    if (grade) params.set('grade', grade);
    if (skill) params.set('skill', skill);
    if (standardCode) params.set('standard', standardCode);
    if (domain) params.set('domain', domain);
    if (studentId) params.set('studentId', String(studentId));
    if (classId) params.set('classId', String(classId));
    const path = (role === 'parent' || role === 'family')
      ? `/parent/at-home-support?${params}`
      : (role === 'admin' ? `/admin/at-home-support?${params}` : `/teacher/at-home-support?${params}`);
    navigate(path, {
      state: {
        returnTo: {
          pathname: location.pathname,
          search: location.search,
          label: '← Back to Student Progress',
          state: {
            ...(location.state || {}),
            selectedStudentId: studentId,
          },
          progressState: {
            expandedSubjects: {
              ...expandedSubjects,
              [subject]: true,
            },
            selectedGradeBySubject: {
              ...selectedGradeBySubject,
              [subject]: grade,
            },
            expandedDomainGrade: nestedContext.domain
              ? `${subject}|${nestedContext.domain}|${grade}`
              : expandedDomainGrade,
            expandedStrand: nestedContext.strand && nestedContext.domain
              ? `${subject}|${nestedContext.domain}|${grade}|${nestedContext.strand}`
              : expandedStrand,
            scrollY: window.scrollY,
          },
        },
      },
    });
  };

  return (
    <div className={showFamilyDescriptions ? 'clear-progress-family' : undefined} style={{ maxWidth: 980, margin: '0 auto', background: UI_COLORS.bg, padding: '8px 4px 22px' }}>
      {previewAsFamily && location.state?.returnTo?.pathname && (
        <PageBackNavigation
          onBack={() => navigate(location.state.returnTo.pathname, { state: location.state.returnTo.state || {} })}
          label={location.state.returnTo.label || 'Back to Student Profiles'} />
      )}
      {previewAsFamily && (
        <div style={{ marginBottom: 14, background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 12, padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#9a3412' }}>
          Previewing as Family — progress reflects this student's family display settings.
        </div>
      )}
      {!snapshotOnly && (
        <div style={{ marginBottom: 18, background: 'var(--ae-surface)', border: '1px solid var(--ae-border)', borderRadius: 18, padding: '18px 18px 14px', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 14, alignItems: 'center', marginBottom: 10 }}>
            <div style={{ width: 90, height: 90, borderRadius: '50%', background: 'linear-gradient(180deg, #e6f0ff 0%, #cde0ff 100%)', border: '2px solid #bfd5f5', color: 'var(--ae-primary-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 30 }}>
              {studentInitials}
            </div>
            <div style={{ background: '#3e5ea8', color: '#fff', borderRadius: 10, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 30, fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1.1 }}>{studentLabel}</div>
                <div style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#dbe7ff', marginTop: 4 }}>
                  Family Learning View
                </div>
              </div>
              {showFamilyDescriptions && studentId && !snapshotOnly && (
                <button
                  type="button"
                  onClick={handleGenerateReport}
                  disabled={isGeneratingReport}
                  style={{ padding: '9px 14px', background: '#fb923c', color: 'white', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: isGeneratingReport ? 'wait' : 'pointer', opacity: isGeneratingReport ? 0.75 : 1 }}
                >
                  {isGeneratingReport ? 'Generating…' : 'Generate Report'}
                </button>
              )}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{studentGradeLabel}</div>
                <div style={{ fontSize: 12, color: '#dbe7ff', marginTop: 3 }}>Progress Snapshot</div>
              </div>
            </div>
          </div>

          {reportError && <div role="alert" style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{reportError}</div>}

          <div style={{ minWidth: 0, marginTop: 4 }}>
            <GradingLegend grayOutRed={grayOutRed} />
          </div>
        </div>
      )}

      {snapshotOnly && focusSnapshot.totalCount > 0 && (
        <div
          onClick={onSnapshotClick || undefined}
          role={onSnapshotClick ? 'button' : undefined}
          tabIndex={onSnapshotClick ? 0 : undefined}
          style={{ marginBottom: 18, background: 'var(--ae-surface)', border: '1px solid var(--ae-border)', borderRadius: 18, padding: '16px 18px', boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)', cursor: onSnapshotClick ? 'pointer' : 'default' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ae-primary-strong)' }}>Progress Snapshot</div>
            {!hidePercent && (
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ae-primary-strong)' }}>
                {focusSnapshot.overallPct}% proficient overall ({focusSnapshot.totalMastered}/{focusSnapshot.totalCount} standards)
              </div>
            )}
          </div>
          {!hidePercent && (
            <div style={{ height: 10, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${focusSnapshot.overallPct}%`, height: '100%', background: UI_COLORS.frame }} />
            </div>
          )}

          {!hidePercent && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: focusSnapshot.topPriorities.length ? 14 : 0 }}>
              {subjectSummaries.map(({ subject, progressPct }) => (
                <div key={`snapshot-${subject}`} style={{ flex: '1 1 140px', background: '#f8fafc', border: '1px solid var(--ae-border)', borderRadius: 12, padding: '10px 12px' }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#334155', marginBottom: 6 }}>{subject}</div>
                  <div style={{ height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                    <div style={{ width: `${progressPct}%`, height: '100%', background: UI_COLORS.frame }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ae-text-muted)', marginTop: 4 }}>{progressPct}% proficient</div>
                </div>
              ))}
            </div>
          )}

          {focusSnapshot.topPriorities.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--ae-primary-strong)', marginBottom: 8, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Suggested Focus &amp; Resources</div>
              <div style={{ fontSize: 12, color: 'var(--ae-text-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                These are the strands that need the most attention right now. Tap one to see guided activities and support ideas.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {focusSnapshot.topPriorities.map((item, idx) => (
                  <button
                    key={`${item.subject}-${item.grade}-${item.label}-${idx}`}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      goToSupport(item.subject, item.grade, item.label, item.standardCode, item.domain);
                    }}
                    style={{
                      border: '1px solid rgba(17, 24, 39, 0.08)',
                      borderRadius: 12,
                      padding: '10px 12px',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      background: displayStatusColor(item.status),
                      color: tileTextColor(item.status, '#111827'),
                      cursor: 'pointer',
                      textAlign: 'left',
                      minWidth: 200,
                      maxWidth: 280,
                    }}
                    title={item.detail || item.label}
                  >
                    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75, marginBottom: 4 }}>
                      {item.subject} • {normalizedGradeLabel(item.grade)}
                    </div>
                    <div>{item.clusterNumber ? `${item.clusterNumber} · ` : ''}{item.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {onSnapshotClick && (
            <div style={{ marginTop: 14, fontSize: 12, fontWeight: 800, color: 'var(--ae-primary-strong)', textAlign: 'right' }}>
              View full progress details →
            </div>
          )}
        </div>
      )}

      {!snapshotOnly && (
      <>
      {!showFamilyDescriptions && <StandardSearch value={standardSearch} onChange={setStandardSearch} />}
      {!showFamilyDescriptions && standardSearch.trim() ? (() => {
        const matches = subjectSummaries.flatMap(({ subject, domains }) =>
          domains.flatMap((domain) => Object.entries(domain.byGrade).flatMap(([grade, cell]) =>
            cell.strands.flatMap((strand) => strand.clusters.flatMap((cluster) =>
              cluster.standards.filter((leaf) => matchesStandardSearch(standardSearch, [
                subject, domain.domain, cell.familyDescription, strand.strand, strand.familyDescription,
                leaf.standardCode, showFamilyDescriptions ? leaf.familyDescription || leaf.detail : leaf.detail,
              ])).map((leaf) => ({ subject, grade, domain: domain.domain, strand: strand.strand, leaf }))
            ))
          ))
        );
        return (
          <section aria-label="Standard search results">
            <p role="status">{matches.length} matching standards.</p>
            {!matches.length && <p>No standards match your search. Try another term or clear the search.</p>}
            <div style={{ display: 'grid', gap: 12 }}>
              {matches.map(({ subject, grade, domain, strand, leaf }) => (
                <article key={`${subject}|${grade}|${domain}|${strand}|${leaf.standardCode}`}
                  style={{ padding: 14, border: '1px solid var(--ae-border)', borderRadius: 12, background: displayStatusColor(leaf.status), overflowWrap: 'anywhere' }}>
                  <div style={{ fontSize: 12, color: 'var(--ae-text-muted)' }}>{subject} · {normalizedGradeLabel(grade)} · {domain} · {strand}</div>
                  <h3 style={{ margin: '8px 0', fontSize: 15 }}>{leaf.standardCode}</h3>
                  <p>
                    {showFamilyDescriptions ? (
                      <HighlightVocabText
                        text={leaf.familyDescription || leaf.detail}
                        subject={subject}
                        vocabBank={vocabBanks[canonicalSubject(subject)]}
                        active={showFamilyDescriptions}
                      />
                    ) : leaf.detail}
                  </p>
                  <DataPointDots history={collectDataPointHistory([{ clusters: [{ standards: [leaf] }] }], showFamilyDescriptions)}
                    getColor={displayStatusColor} hideScore={hidePercent} showPrivateNotes={!isRestrictedAudience}
                    label={`Data points for ${leaf.standardCode}`} />
                  <button type="button" aria-expanded={selectedSupportStandard === leaf.standardCode}
                    onClick={() => setSelectedSupportStandard((current) => current === leaf.standardCode ? null : leaf.standardCode)}
                    style={{ marginTop: 10, padding: '8px 12px', border: '1px solid #bfdbfe', borderRadius: 8, background: '#fff', color: '#1d4ed8', cursor: 'pointer' }}>
                    {showFamilyDescriptions ? 'Guided Action' : 'Action Card'}
                  </button>
                  {selectedSupportStandard === leaf.standardCode && (
                    <InlineAtHomeSupportPanel studentId={studentId} classId={classId}
                      standard={leaf.standardCode} grade={grade} subject={subject}
                      strand={leaf.strand} domain={leaf.domain} status={leaf.status}
                      useFamilySupport={showFamilyDescriptions} familySupport={leaf.familySupport} />
                  )}
                </article>
              ))}
            </div>
          </section>
        );
      })() : (
      <div style={{ border: `1.5px solid ${UI_COLORS.frame}`, borderRadius: 14, padding: 14, background: '#eef3fb', boxShadow: '0 14px 34px rgba(47, 92, 169, 0.08)' }}>
        {subjectSummaries.map(({ subject, grades, displayGrades = grades, domains }) => {
          const isSubjectExpanded = !!expandedSubjects[subject];
          const defaultGrade = displayGrades.find((grade) => (
              gradeFloor(grade.grade) === gradeFloor(studentGrade)
              || isGradeCoveredByBand(studentGrade, grade.grade)
            ))
            || displayGrades.find((grade) => (grade.counts?.working || 0) > 0)
            || displayGrades[0];
          const selectedGrade = selectedGradeBySubject[subject] || defaultGrade?.grade;
          const visibleGrades = displayGrades.filter((grade) => grade.grade === selectedGrade);
          const visibleDomains = domains.filter((domainRow) => selectedGrade && domainRow.byGrade[selectedGrade]);
          const selectedGradeHasStandards = visibleGrades.some((grade) => grade.counts.total > 0);
          const ireadySubject = IREADY_SUBJECT_BY_PANEL[String(subject || '').toLowerCase()];
          return (
          <section key={subject} style={{ marginBottom: 16, background: '#dce8f7', borderRadius: 14, padding: '10px 12px 14px', border: '1px solid #c7d7ef' }}>
            <button
              type="button"
              onClick={() => toggleSubjectExpanded(subject)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: UI_COLORS.frame,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontWeight: 800,
                fontSize: 24,
                letterSpacing: 0.2,
                marginBottom: 12,
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
                cursor: 'pointer',
              }}
              aria-expanded={isSubjectExpanded}
              title={isSubjectExpanded ? 'Collapse domain breakdown' : 'Expand to see the domain breakdown by grade'}
            >
              {subject}
              <span style={{ fontSize: 16, transform: isSubjectExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
            </button>

            <div
              ref={(element) => {
                if (element) gradeCarouselRefs.current[subject] = element;
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, overflowX: 'auto', padding: '10px max(12px, calc(50% - 76px)) 14px', margin: '0 -2px 12px', scrollbarWidth: 'thin', scrollSnapType: 'x mandatory' }}
            >
              {displayGrades.map((g) => {
                const isSelectedGrade = g.grade === selectedGrade;
                const gradeLabel = normalizedGradeLabel(g.grade);
                const domainMarkers = domains
                  .filter((domainRow) => domainRow.byGrade[g.grade])
                  .map((domainRow) => ({ key: domainRow.domain, name: domainRow.domain, status: summaryStatus(domainRow.byGrade[g.grade]) }));
                const domainMarkersId = `grade-domains-${[studentId, subject, g.grade].map((part) => String(part ?? '').replace(/\W+/g, '-')).join('-')}`;
                return (
                <div key={`${subject}-${g.grade}-bar`} style={{ display: 'contents' }}>
                <button
                  type="button"
                  className="clear-progress-grade-card"
                  ref={(element) => {
                    if (element) gradeTileRefs.current[`${subject}|${g.grade}`] = element;
                  }}
                  onClick={() => {
                    if (isSelectedGrade && isSubjectExpanded) {
                      toggleSubjectExpanded(subject);
                      return;
                    }
                    setSelectedGradeBySubject((previous) => ({ ...previous, [subject]: g.grade }));
                    if (!isSubjectExpanded) toggleSubjectExpanded(subject);
                  }}
                  aria-label={[gradeLabel, g.assessmentLock?.locked ? 'Locked' : ''].filter(Boolean).join(' ')}
                  aria-describedby={domainMarkers.length ? domainMarkersId : undefined}
                  style={{ flex: `0 0 ${isSelectedGrade ? 150 : 104}px`, minHeight: isSelectedGrade ? 92 : 72, display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'center', gap: isSelectedGrade ? 6 : 4, background: displayStatusColor(summaryStatus(g)), border: '1px solid rgba(15, 23, 42, 0.08)', borderRadius: 14, padding: isSelectedGrade ? '12px 10px' : '9px 8px', boxShadow: isSelectedGrade ? '0 10px 22px rgba(15, 23, 42, 0.16)' : '0 6px 14px rgba(15, 23, 42, 0.05)', cursor: 'pointer', textAlign: 'left', transform: isSelectedGrade ? 'scale(1.08)' : 'scale(0.94)', opacity: isSelectedGrade ? 1 : 0.82, scrollSnapAlign: 'center', transition: 'flex-basis 0.2s ease, transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease' }}
                  title={isSelectedGrade && isSubjectExpanded
                    ? 'Click to collapse this grade'
                    : 'Click to show this grade’s domains'}
                >
                  <div style={{ fontSize: isSelectedGrade ? 12 : 10, fontWeight: 800, lineHeight: 1.2, color: tileTextColor(summaryStatus(g), 'var(--ae-primary-strong)'), textAlign: 'center' }}>
                    {gradeLabel}
                  </div>
                  {domainMarkers.length > 0 && (
                    <div className="clear-progress-grade-pill-tray">
                      <StatusMarkers
                        items={domainMarkers}
                        getColor={displayStatusColor}
                        variant={isSelectedGrade ? 'gradePill' : 'gradePillCompact'}
                        align="center"
                        label={`${subject} ${gradeLabel} domain status`}
                        decorative
                      />
                    </div>
                  )}
                  {g.assessmentLock?.locked && (
                    <div style={{ fontSize: 10, fontWeight: 800, color: tileTextColor(summaryStatus(g), '#7f1d1d'), textAlign: 'center' }} title={g.assessmentLock.reason}>
                      Locked
                    </div>
                  )}
                </button>
                {domainMarkers.length > 0 && (
                  <span id={domainMarkersId} hidden>
                    {domainMarkers.map((marker) => `${marker.name}: ${STATUS_MARKER_LABELS[marker.status] || STATUS_MARKER_LABELS.na}`).join(', ')}
                  </span>
                )}
                </div>
                );
              })}
            </div>
           
            {isFamilyLikeView && studentId && ireadySubject && isSubjectExpanded && selectedGradeHasStandards
              && selectedGradeBySubject[subject] === selectedGrade && (
                <div style={{ background: 'var(--ae-surface)', border: '1px solid #d7e1ef', borderRadius: 12, padding: '4px 12px 10px', marginBottom: 12, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)' }}>
                    <IreadyScoreBoxes studentId={studentId} subjects={[ireadySubject]} showInfoTooltip={isRestrictedAudience} />
                </div>
            )}

            {isSubjectExpanded && (
              <div className="clear-progress-domain-list" style={{ display: 'grid', gap: 10, marginBottom: 12, padding: 10, background: '#c9dbf2', border: '1px solid #b4cbea', borderRadius: 14, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)' }}>
                {visibleDomains.map((domainRow) => (
                  <div key={`${subject}-${domainRow.domain}`} className="clear-progress-domain-card" style={{ minWidth: 0, background: 'var(--ae-surface)', border: '1px solid #d7e1ef', borderRadius: 12, padding: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.05)' }}>
                    {visibleGrades.map((g) => {
                      const cell = domainRow.byGrade[g.grade];
                      const cellKey = `${subject}|${domainRow.domain}|${g.grade}`;
                      const isCellOpen = expandedDomainGrade === cellKey;
                      const strandMarkers = cell ? cell.strands.map((strandRow) => ({ key: strandRow.strand, name: strandRow.strand, status: summaryStatus(strandRow) })) : [];
                      return (
                        <div key={`${cellKey}-header`} className={`clear-progress-header-tile${strandMarkers.length ? ' clear-progress-header-tile-with-points' : ''}`} style={{ background: cell ? displayStatusColor(summaryStatus(cell)) : 'var(--ae-surface-soft)', border: isCellOpen ? '2px solid #1e3a5f' : '1px solid rgba(15, 23, 42, 0.08)', borderRadius: 9 }}>
                        <button
                          type="button"
                          disabled={!cell}
                          onClick={() => toggleDomainGrade(subject, domainRow.domain, g.grade)}
                          style={{ width: '100%', minWidth: 0, display: 'grid', gridTemplateColumns: showFamilyDescriptions ? (hidePercent ? 'minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto auto') : (hidePercent ? 'minmax(0, 1fr) auto' : 'minmax(0, 0.42fr) minmax(0, 1fr) auto'), alignItems: 'center', gap: 10, background: cell ? displayStatusColor(summaryStatus(cell)) : 'var(--ae-surface-soft)', border: 'none', borderRadius: 9, padding: '9px 10px', cursor: cell ? 'pointer' : 'default', opacity: cell ? 1 : 0.45, textAlign: 'left' }}
                          title={cell ? `${domainRow.domain} · ${normalizedGradeLabel(g.grade)} · click to see strands` : `No ${domainRow.domain} standards recorded for this grade yet.`}
                        >
                          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            {!showFamilyDescriptions && <span className="clear-progress-level-label">Domain</span>}
                            <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: tileTextColor(summaryStatus(cell), 'var(--ae-primary-strong)') }}>{domainRow.domain}</span>
                            {showFamilyDescriptions && cell?.familyDescription && (
                              <span style={{ display: 'block', marginTop: 5, fontSize: 12, fontWeight: 500, lineHeight: 1.55, color: tileTextColor(summaryStatus(cell), 'var(--ae-text-muted)') }}>
                                <HighlightVocabText
                                  text={cell.familyDescription}
                                  subject={subject}
                                  vocabBank={vocabBanks[canonicalSubject(subject)]}
                                  active={showFamilyDescriptions}
                                />
                              </span>
                            )}
                          </span>
                          {!hidePercent && hasAssessedProgress(cell.counts) && <span style={{ fontSize: 10, fontWeight: 800, color: tileTextColor(summaryStatus(cell), '#111827'), textAlign: 'center' }}>{pctMastered(cell.counts)}% proficient</span>}
                          <span style={{ fontSize: 11, color: tileTextColor(summaryStatus(cell), 'var(--ae-text-muted)'), transform: isCellOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                        </button>
                        {strandMarkers.length > 0 && (
                          <div className="clear-progress-header-points">
                            <StatusMarkers items={strandMarkers} getColor={displayStatusColor} variant="pill" label={`Strand status: ${domainRow.domain}`} />
                          </div>
                        )}
                        </div>
                      );
                    })}

                    {visibleGrades.map((g) => {
                      const cell = domainRow.byGrade[g.grade];
                      const cellKey = `${subject}|${domainRow.domain}|${g.grade}`;
                      if (expandedDomainGrade !== cellKey || !cell) return null;
                      return (
                        <div key={`${cellKey}-strands`} style={{ minWidth: 0, marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5edf7' }}>
                          {!showFamilyDescriptions && (
                            <div style={{ minWidth: 0, fontSize: 11, fontWeight: 800, color: 'var(--ae-text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em', overflowWrap: 'anywhere' }}>
                              {domainRow.domain} · {normalizedGradeLabel(g.grade)} strands
                            </div>
                          )}
                          <div className="clear-progress-strand-list" style={{ display: 'grid', gap: 8 }}>
                            {cell.strands.map((strandRow) => {
                              const strandKey = `${cellKey}|${strandRow.strand}`;
                              const isStrandOpen = expandedStrand === strandKey;
                              const standardMarkers = strandRow.clusters.flatMap((clusterRow) => clusterRow.standards.map((leaf) => ({ key: `${clusterRow.cluster}|${leaf.standardCode || leaf.label}`, name: leaf.label || leaf.standardCode, status: leaf.status })));
                              return (
                                <div key={strandKey} className="clear-progress-strand-card" style={{ minWidth: 0, background: '#f8fbff', border: '1px solid var(--ae-border)', borderRadius: 10, padding: 8, overflow: 'hidden' }}>
                                  <div className={`clear-progress-header-tile${standardMarkers.length ? ' clear-progress-header-tile-with-points' : ''}`} style={{ background: showFamilyDescriptions ? displayStatusColor(summaryStatus(strandRow)) : undefined, borderRadius: 8 }}>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (cell.assessmentLock?.locked) return;
                                      toggleStrand(cellKey, strandRow.strand);
                                    }}
                                    style={{ width: '100%', minWidth: 0, display: showFamilyDescriptions ? 'grid' : 'flex', gridTemplateColumns: showFamilyDescriptions ? 'minmax(0, 1fr) auto' : undefined, alignItems: 'center', gap: 10, background: showFamilyDescriptions ? displayStatusColor(summaryStatus(strandRow)) : 'none', border: 'none', borderRadius: 8, padding: showFamilyDescriptions ? '8px 10px' : 0, cursor: cell.assessmentLock?.locked ? 'default' : 'pointer', textAlign: 'left' }}
                                    title={cell.assessmentLock?.locked ? cell.assessmentLock.reason : `${strandRow.strand} · click to see standards`}
                                  >
                                    <div style={{ flex: showFamilyDescriptions ? undefined : '0 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>
                                      {!showFamilyDescriptions && <span className="clear-progress-level-label">Strand</span>}
                                      <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: tileTextColor(summaryStatus(strandRow), '#1e3a5f') }}>{strandRow.strand}</span>
                                      {showFamilyDescriptions && strandRow.familyDescription && (
                                        <span style={{ display: 'block', marginTop: 5, fontSize: 12, fontWeight: 500, lineHeight: 1.55, color: tileTextColor(summaryStatus(strandRow), 'var(--ae-text-muted)') }}>
                                          <HighlightVocabText
                                            text={strandRow.familyDescription}
                                            subject={subject}
                                            vocabBank={vocabBanks[canonicalSubject(subject)]}
                                            active={showFamilyDescriptions}
                                          />
                                        </span>
                                      )}
                                    </div>
                                    {!hidePercent && (
                                      <div style={{ flex: showFamilyDescriptions ? undefined : '1 1 120px', gridColumn: showFamilyDescriptions ? '1 / -1' : undefined, minWidth: 0 }}>
                                        <SegmentedProgressBar status={summaryStatus(strandRow)} counts={strandRow.counts} getColor={displayStatusColor} height={12} />
                                      </div>
                                    )}
                                    <span style={{ fontSize: 12, color: tileTextColor(summaryStatus(strandRow), 'var(--ae-text-muted)'), transform: isStrandOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                                  </button>
                                  {standardMarkers.length > 0 && (
                                    <div className="clear-progress-header-points">
                                      <StatusMarkers items={standardMarkers} getColor={displayStatusColor} label={`Standard status: ${strandRow.strand}`} />
                                    </div>
                                  )}
                                  </div>

                                  {isStrandOpen && (
                                    <div className="clear-progress-standard-list" style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--ae-border)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 6 }}>
                                      {strandRow.clusters.flatMap((clusterRow) => (
                                        clusterRow.standards.map((leaf) => {
                                          const isSupportOpen = selectedSupportStandard === leaf.standardCode;
                                          const standardDescription = showFamilyDescriptions && leaf.familyDescription
                                            ? leaf.familyDescription
                                            : leaf.detail;
                                          return (
                                            <div key={`${strandKey}|${clusterRow.cluster}|${leaf.standardCode || leaf.label}`} className="clear-progress-standard-item" style={{ minWidth: 0, gridColumn: '1 / -1' }}>
                                              <div className={`clear-progress-standard-card${showFamilyDescriptions && leaf.history?.length ? ' clear-progress-standard-card-with-points' : ''}`} style={{
                                                border: '1px solid rgba(17, 24, 39, 0.08)',
                                                borderRadius: 10,
                                                background: displayStatusColor(leaf.status),
                                                color: tileTextColor(leaf.status, '#111827'),
                                                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.08)',
                                                minWidth: 0,
                                                overflow: 'hidden',
                                              }}>
                                              <button
                                                type="button"
                                                aria-expanded={isSupportOpen}
                                                onClick={() => setSelectedSupportStandard((current) => current === leaf.standardCode ? null : leaf.standardCode)}
                                                style={{
                                                      border: 'none',
                                                      padding: showFamilyDescriptions ? '8px 10px' : '6px 10px',
                                                      fontSize: 11,
                                                      fontWeight: 800,
                                                      lineHeight: 1.2,
                                                      background: 'transparent',
                                                      color: tileTextColor(leaf.status, showFamilyDescriptions ? 'var(--ae-text-muted)' : '#111827'),
                                                      cursor: 'pointer',
                                                      textAlign: 'left',
                                                      whiteSpace: 'normal',
                                                      width: '100%',
                                                      minWidth: 0,
                                                      maxWidth: '100%',
                                                      boxSizing: 'border-box',
                                                      overflowWrap: 'anywhere',
                                                      wordBreak: 'break-word',
                                                      overflow: 'hidden',
                                                }}
                                                title={standardDescription || leaf.label}
                                              >
                                                {!showFamilyDescriptions && (
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                                    <div style={{ fontSize: 9, opacity: 0.72 }}>{leaf.label}</div>
                                                  </div>
                                                )}
                                                <div style={showFamilyDescriptions ? { fontSize: 12, fontWeight: 500, lineHeight: 1.55 } : undefined}>
                                                  {showFamilyDescriptions ? (
                                                    <HighlightVocabText
                                                      text={standardDescription || leaf.label}
                                                      subject={subject}
                                                      vocabBank={vocabBanks[canonicalSubject(subject)]}
                                                      active={showFamilyDescriptions}
                                                    />
                                                  ) : (
                                                    standardDescription || leaf.label
                                                  )}
                                                </div>
                                              </button>
                                              {leaf.history?.length > 0 && (
                                                <div className={showFamilyDescriptions ? 'clear-progress-standard-points' : undefined} style={showFamilyDescriptions ? undefined : { padding: '0 10px 6px' }}>
                                                  <DataPointDots history={leaf.history} getColor={displayStatusColor} hideScore={hidePercent} showPrivateNotes={!isRestrictedAudience} />
                                                </div>
                                              )}
                                              </div>
                                              {isSupportOpen && (
                                                <InlineAtHomeSupportPanel
                                                  studentId={studentId}
                                                  classId={classId}
                                                  standard={leaf.standardCode}
                                                  grade={g.grade}
                                                  subject={subject}
                                                  strand={leaf.strand}
                                                  domain={leaf.domain}
                                                  status={leaf.status}
                                                  useFamilySupport={showFamilyDescriptions}
                                                  familySupport={leaf.familySupport}
                                                  canManageSchoolResources={role === 'teacher' && previewAsFamily && !isReadOnlyTeacherDemo(user)}
                                                />
                                              )}
                                            </div>
                                          );
                                        })
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

          </section>
          );
        })}
      </div>

      )}
      {subjectSummaries.length === 0 && (
        <div style={{ color: 'var(--ae-text-muted)', fontSize: 14, textAlign: 'center', padding: 32 }}>
          No progress data recorded yet for this student.
        </div>
      )}
      {previewAsFamily && location.state?.returnTo?.pathname && (
        <PageBackNavigation position="bottom"
          onBack={() => navigate(location.state.returnTo.pathname, { state: location.state.returnTo.state || {} })}
          label={location.state.returnTo.label || 'Back to Student Profiles'} />
      )}
      <BackToTop />
      </>
      )}
    </div>
  );
}